import { getMplTokenAuthRulesProgramId } from "@metaplex-foundation/mpl-candy-machine";
import { fetchDigitalAssetWithAssociatedToken, findTokenRecordPda, TokenStandard, transferV1 } from "@metaplex-foundation/mpl-token-metadata";
import { type Instruction as UmiInstruction, publicKey, Umi, unwrapOptionRecursively } from "@metaplex-foundation/umi";
import { publicKey as publicKeyUmiSerializer } from "@metaplex-foundation/umi/serializers";
import {
  type AccountInfoBase,
  type AccountInfoWithBase64EncodedData,
  type AccountMeta,
  AccountRole,
  type Address,
  address,
  appendTransactionMessageInstructions,
  compileTransaction,
  createNoopSigner,
  createTransactionMessage,
  decompileTransactionMessageFetchingLookupTables,
  getBase16Decoder,
  getBase64Decoder,
  getBase64EncodedWireTransaction,
  getBase64Encoder,
  getCompiledTransactionMessageDecoder,
  getTransactionDecoder,
  type Instruction,
  parseBase64RpcAccount,
  pipe,
  type Rpc,
  setTransactionMessageFeePayer,
  setTransactionMessageLifetimeUsingBlockhash,
  type SolanaRpcApi,
  type Transaction,
  type TransactionMessageBytesBase64,
} from "@solana/kit";
import { getSetComputeUnitLimitInstruction, getSetComputeUnitPriceInstruction } from "@solana-program/compute-budget";
import { ParsedStakeInstruction, parseStakeInstruction, STAKE_PROGRAM_ADDRESS, StakeInstruction } from "@solana-program/stake";
import {
  getTransferSolInstruction,
  ParsedSystemInstruction,
  parseSystemInstruction,
  SYSTEM_PROGRAM_ADDRESS,
  SystemInstruction,
} from "@solana-program/system";
import {
  decodeToken,
  fetchMaybeMint,
  findAssociatedTokenPda,
  getCreateAssociatedTokenIdempotentInstruction,
  getTokenSize,
  getTransferCheckedInstruction,
  ParsedTokenInstruction,
  parseTokenInstruction,
  TOKEN_PROGRAM_ADDRESS,
  TokenInstruction,
} from "@solana-program/token";
import {
  decodeToken as decodeToken2022,
  ParsedToken2022Instruction,
  parseToken2022Instruction,
  TOKEN_2022_PROGRAM_ADDRESS,
  Token2022Instruction,
} from "@solana-program/token-2022";
import { type BaseTokenInfo, type ContractExchangeRates } from "@toruslabs/base-controllers";
import { CustomTokenInfo, LAMPORTS_PER_SOL, SolanaNftInfo } from "@toruslabs/solana-controllers";
import log from "loglevel";

export type SolanaDecodedDataType = {
  type: string;
  programId: string;
  data: { [key: string]: string | number | bigint | undefined | null };
};

export interface SolanaAccountEstimation {
  name: string;
  symbol: string;
  logo: string;
  changes: number;
  changesInSol: number;
  mint: string;
  address: string;
  decimals: number;
}

// ============================================================================
// Instruction Type Constants
// ============================================================================

export const SOLANA_INSTRUCTION_TYPES = {
  // System Program
  SYSTEM_TRANSFER_SOL: `system${SystemInstruction[SystemInstruction.TransferSol]}`,
  // Token Program (SPL) — prefixed to avoid collision with Token-2022 (same enum names)
  TOKEN_TRANSFER: `token:${TokenInstruction[TokenInstruction.Transfer]}`,
  TOKEN_TRANSFER_CHECKED: `token:${TokenInstruction[TokenInstruction.TransferChecked]}`,
  // Token-2022 Program
  TOKEN_2022_TRANSFER: `token2022:${Token2022Instruction[Token2022Instruction.Transfer]}`,
  TOKEN_2022_TRANSFER_CHECKED: `token2022:${Token2022Instruction[Token2022Instruction.TransferChecked]}`,
} as const;

// ============================================================================
// Instruction Decoders
// ============================================================================

export const decodeUnknownInstruction = (instruction: Instruction): SolanaDecodedDataType => {
  return {
    type: "Unknown",
    programId: instruction.programAddress,
    data: {
      data: instruction.data ? getBase16Decoder().decode(instruction.data) : "",
    },
  };
};

function flattenParsedInstruction(
  typeName: string,
  parsed: ParsedStakeInstruction<string> | ParsedSystemInstruction<string> | ParsedTokenInstruction<string> | ParsedToken2022Instruction<string>
): SolanaDecodedDataType {
  const result: SolanaDecodedDataType["data"] = {};

  if ("accounts" in parsed && typeof parsed.accounts === "object") {
    for (const [key, meta] of Object.entries(parsed.accounts as Record<string, unknown>)) {
      if (meta && typeof meta === "object" && "address" in meta) {
        result[key] = (meta as { address: string }).address;
      }
    }
  }

  if (parsed.data && typeof parsed.data === "object") {
    for (const [key, value] of Object.entries(parsed.data as Record<string, unknown>)) {
      if (key === "discriminator") continue;
      if (typeof value === "string" || typeof value === "number" || typeof value === "bigint") {
        result[key] = value;
      } else if (value === null || value === undefined) {
        result[key] = value as null;
      } else if (typeof value === "object" && value !== null && "__option" in value) {
        const opt = value as { __option: string; value?: unknown };
        result[key] = opt.__option === "Some" ? (opt.value as string) : null;
      }
    }
  }

  return { type: typeName, programId: parsed.programAddress as string, data: result };
}

export const decodeSystemInstruction = (inst: Instruction): SolanaDecodedDataType => {
  if (!inst.data || inst.data.length < 4) return decodeUnknownInstruction(inst);
  try {
    const parsed = parseSystemInstruction(inst as Parameters<typeof parseSystemInstruction>[0]);
    return flattenParsedInstruction(`system${SystemInstruction[parsed.instructionType]}`, parsed);
  } catch {
    return decodeUnknownInstruction(inst);
  }
};

export const decodeStakeInstruction = (inst: Instruction): SolanaDecodedDataType => {
  if (!inst.data || inst.data.length < 4) return decodeUnknownInstruction(inst);
  try {
    const parsed = parseStakeInstruction(inst as Parameters<typeof parseStakeInstruction>[0]);
    return flattenParsedInstruction(`stake${StakeInstruction[parsed.instructionType]}`, parsed);
  } catch {
    return decodeUnknownInstruction(inst);
  }
};

export function decodeTokenInstruction(instruction: Instruction): SolanaDecodedDataType {
  if (!instruction.data || instruction.data.length < 1) return decodeUnknownInstruction(instruction);
  try {
    const parsed = parseTokenInstruction(instruction as Parameters<typeof parseTokenInstruction>[0]);
    return flattenParsedInstruction(`token:${TokenInstruction[parsed.instructionType]}`, parsed);
  } catch {
    return decodeUnknownInstruction(instruction);
  }
}

export function decodeToken2022Instruction(instruction: Instruction): SolanaDecodedDataType {
  if (!instruction.data || instruction.data.length < 1) return decodeUnknownInstruction(instruction);
  try {
    const parsed = parseToken2022Instruction(instruction as Parameters<typeof parseToken2022Instruction>[0]);
    return flattenParsedInstruction(`token2022:${Token2022Instruction[parsed.instructionType]}`, parsed);
  } catch {
    return decodeUnknownInstruction(instruction);
  }
}

export const decodeInstruction = (instruction: Instruction): SolanaDecodedDataType => {
  try {
    const progAddr = instruction.programAddress;
    if (progAddr === SYSTEM_PROGRAM_ADDRESS) {
      return decodeSystemInstruction(instruction);
    }
    if (progAddr === STAKE_PROGRAM_ADDRESS) {
      return decodeStakeInstruction(instruction);
    }
    if (progAddr === TOKEN_PROGRAM_ADDRESS) {
      return decodeTokenInstruction(instruction);
    }
    if (progAddr === TOKEN_2022_PROGRAM_ADDRESS) {
      return decodeToken2022Instruction(instruction);
    }
  } catch (err) {
    log.error(err);
  }
  return decodeUnknownInstruction(instruction);
};

// ============================================================================
// Transaction Decoding & Decompiling Utilities
// ============================================================================

/** Extract base64-encoded message from a decoded transaction for fee estimation */
export function getMessageBytesBase64(tx: Transaction): TransactionMessageBytesBase64 {
  return getBase64Decoder().decode(tx.messageBytes) as TransactionMessageBytesBase64;
}

/** Decode a base64 transaction string into a Transaction */
export function decodeTransaction(base64Transaction: string): { tx: Transaction } {
  if (!base64Transaction) {
    throw new Error("Invalid transaction: expected non-empty base64 string");
  }
  try {
    const tx = getTransactionDecoder().decode(getBase64Encoder().encode(base64Transaction));
    return { tx };
  } catch (e) {
    throw new Error("Failed to decode transaction: malformed base64 or invalid format", { cause: e });
  }
}

/** Decompile a transaction's instructions, resolving ALT accounts via RPC if present */
export async function getDecompiledInstructions(tx: Transaction, rpc: Rpc<SolanaRpcApi>): Promise<Instruction[]> {
  const compiledMsg = getCompiledTransactionMessageDecoder().decode(tx.messageBytes);
  const decompiledMsg = await decompileTransactionMessageFetchingLookupTables(compiledMsg, rpc);
  return (decompiledMsg.instructions ?? []) as Instruction[];
}

// Calculate balance changes after transaction simulation
const calculateChanges = async ({
  rpc,
  simulatedAccounts,
  selectedAddress,
  accountKeys,
  tokensState,
  tokenRates,
  networkFee,
  hasSolTransferInstruction,
}: {
  rpc: Rpc<SolanaRpcApi>;
  simulatedAccounts: Array<(AccountInfoBase & AccountInfoWithBase64EncodedData) | null>;
  selectedAddress: string;
  accountKeys: string[];
  tokensState: BaseTokenInfo[];
  tokenRates: ContractExchangeRates;
  networkFee: number;
  hasSolTransferInstruction: boolean;
}): Promise<SolanaAccountEstimation[]> => {
  const returnResult: SolanaAccountEstimation[] = [];
  const postTokenDetails: Array<{ address: string; mint: string; owner: string; amount: bigint; isToken2022: boolean }> = [];

  // For all account changes, filter out Token Accounts (post tx)
  simulatedAccounts.forEach((item, idx) => {
    if (!item) return;

    const encoded = parseBase64RpcAccount(address(accountKeys[idx]), item);
    const isToken2022 = item.owner === TOKEN_2022_PROGRAM_ADDRESS;
    const isTokenProgram = item.owner === TOKEN_PROGRAM_ADDRESS || isToken2022;

    // Check if this is a token account (not a mint) by verifying minimum token account size
    if (isTokenProgram && encoded.data.length >= getTokenSize()) {
      // Use appropriate decoder based on token program
      const { data: token } = isToken2022 ? decodeToken2022(encoded) : decodeToken(encoded);
      postTokenDetails.push({
        address: accountKeys[idx],
        mint: token.mint,
        owner: token.owner,
        amount: token.amount,
        isToken2022,
      });
    }
  });

  // Query current token accountInfo (signer account + token accounts for pre-tx state)
  const queryAddresses: Address[] = [address(selectedAddress), ...postTokenDetails.map((item) => address(item.address))];
  const queryResult = await rpc.getMultipleAccounts(queryAddresses, { encoding: "base64" }).send();
  const queryAccounts = queryResult.value;
  const signerAccount = queryAccounts[0];
  const tokenAccounts = queryAccounts.slice(1);

  const preTokenDetails = tokenAccounts.map((item, idx) => {
    if (!item) return null;
    const encoded = parseBase64RpcAccount(address(postTokenDetails[idx].address), item);
    // Use the appropriate decoder based on the token program (tracked from post-tx state)
    const { isToken2022 } = postTokenDetails[idx];
    return (isToken2022 ? decodeToken2022(encoded) : decodeToken(encoded)).data;
  });

  // Check for holder's sol account balance changes
  const accIdx = accountKeys.findIndex((item) => item === selectedAddress);
  if (accIdx >= 0) {
    const postLamports = simulatedAccounts.at(accIdx)?.lamports || 0n;
    const preLamports = signerAccount?.lamports || 0n;
    const solchanges = (Number(postLamports) - Number(preLamports) + Number(networkFee)) / Number(LAMPORTS_PER_SOL);
    if (hasSolTransferInstruction || solchanges > 0) {
      returnResult.push({
        changes: solchanges,
        symbol: "SOL",
        name: "Solana",
        logo: "solana.svg",
        changesInSol: solchanges,
        mint: "",
        address: selectedAddress,
        decimals: 9,
      });
    }
  }

  // Calculate token account changes
  for (const [idx, item] of postTokenDetails.entries()) {
    const preTokenOwner = preTokenDetails[idx]?.owner;
    const postTokenOwner = item.owner;
    if (preTokenOwner === selectedAddress || postTokenOwner === selectedAddress) {
      let mint = item.mint;
      const tokenInfo = tokensState?.find((token) => token.tokenAddress === mint);
      const symbol = tokenInfo?.symbol || "Unknown";
      const name = tokenInfo?.name || "Unknown";
      const logo = tokenInfo?.logo || "unknown.svg";

      // Get decimals from tokenInfo if available, otherwise fetch from mint account
      let decimals: number = typeof tokenInfo?.decimals === "number" ? tokenInfo.decimals : Number(tokenInfo?.decimals) || 9;
      const preTokenMint = preTokenDetails[idx]?.mint;
      if (!tokenInfo && mint && mint !== SYSTEM_PROGRAM_ADDRESS) {
        // Token not in known state, fetch decimals from mint
        const mintResult = await fetchMaybeMint(rpc, address(mint));
        if (mintResult.exists) {
          decimals = mintResult.data.decimals;
        }
      } else if (!tokenInfo && preTokenMint && preTokenMint !== SYSTEM_PROGRAM_ADDRESS) {
        // Try pre-tx mint if post-tx mint unavailable
        mint = preTokenMint;
        const mintResult = await fetchMaybeMint(rpc, address(preTokenMint));
        if (mintResult.exists) {
          decimals = mintResult.data.decimals;
        }
      }

      let postAmount = item.amount;
      if (postTokenOwner !== selectedAddress) postAmount = 0n;

      let preTokenAmount = preTokenDetails[idx]?.amount || 0n;
      if (preTokenOwner !== selectedAddress) preTokenAmount = 0n;

      const changes = Number(postAmount - preTokenAmount) / 10 ** decimals;
      const tokenRate = changes * (tokenRates[mint] || 0);
      const changesInSol = changes * tokenRate;
      returnResult.push({
        changes,
        changesInSol,
        symbol,
        name,
        mint,
        logo,
        address: item.address,
        decimals,
      });
    }
  }

  log.info(returnResult);
  return returnResult;
};

// Simulate transaction's balance changes
export const getEstimateBalanceChange = async ({
  rpc,
  tx,
  signer,
  tokensState,
  tokenRates,
  networkFee,
  hasSolTransferInstruction,
}: {
  rpc: Rpc<SolanaRpcApi>;
  tx: Transaction;
  signer: string;
  tokensState: BaseTokenInfo[];
  tokenRates: ContractExchangeRates;
  networkFee: number;
  hasSolTransferInstruction: boolean;
}): Promise<SolanaAccountEstimation[]> => {
  try {
    // Encode transaction for simulation
    const base64Tx = getBase64EncodedWireTransaction(tx);

    // Collect all writable accounts from the compiled message for simulation tracking.
    // The static accounts layout is: [writable signers | readonly signers | writable non-signers | readonly non-signers]
    const compiledMsg = getCompiledTransactionMessageDecoder().decode(tx.messageBytes);
    const { numSignerAccounts, numReadonlySignerAccounts, numReadonlyNonSignerAccounts } = compiledMsg.header;
    const numWritableSigners = numSignerAccounts - numReadonlySignerAccounts;
    const firstReadonlyNonSignerIdx = compiledMsg.staticAccounts.length - numReadonlyNonSignerAccounts;
    const addresses: Address[] = [
      ...compiledMsg.staticAccounts.slice(0, numWritableSigners),
      ...compiledMsg.staticAccounts.slice(numSignerAccounts, firstReadonlyNonSignerIdx),
    ];
    // Ensure the signer is always included (e.g. if fee payer is not in writable signers for some reason)
    const signerAddr = address(signer);
    if (!addresses.includes(signerAddr)) addresses.push(signerAddr);
    const result = await rpc
      .simulateTransaction(base64Tx, {
        encoding: "base64",
        accounts: { addresses, encoding: "base64" },
      })
      .send();

    if (result.value.err) {
      const errStr = JSON.stringify(result.value.err, (_key, value) => (typeof value === "bigint" ? value.toString() : value));
      throw new Error(`Transaction simulation failed: ${errStr}`);
    }

    return calculateChanges({
      rpc,
      simulatedAccounts: result.value.accounts || [],
      selectedAddress: signer,
      accountKeys: addresses,
      tokensState,
      tokenRates,
      networkFee,
      hasSolTransferInstruction,
    });
  } catch (e) {
    log.warn(e);
    throw new Error("Failed to simulate transaction for balance change", { cause: e });
  }
};

// ============================================================================
// Transaction Builders
// ============================================================================

export async function generateSolTransaction(receiver: string, amount: bigint, sender: string, rpc: Rpc<SolanaRpcApi>) {
  if (!rpc) {
    throw new Error("RPC not found");
  }

  const senderSigner = createNoopSigner(address(sender));
  const transferIx = getTransferSolInstruction({
    source: senderSigner,
    destination: address(receiver),
    amount,
  });

  const { value: latestBlockhash } = await rpc.getLatestBlockhash({ commitment: "finalized" }).send();

  const msg = pipe(
    createTransactionMessage({ version: 0 }),
    (m) => setTransactionMessageFeePayer(address(sender), m),
    (m) => setTransactionMessageLifetimeUsingBlockhash(latestBlockhash, m),
    (m) => appendTransactionMessageInstructions([transferIx], m)
  );

  return compileTransaction(msg);
}

export function isCustomTokenInfo(token: CustomTokenInfo | SolanaNftInfo): token is CustomTokenInfo {
  return (token as CustomTokenInfo).tokenAddress !== undefined;
}

/**
 * Converts a Umi instruction to a Kit instruction.
 * Custom adapter to replace \@metaplex-foundation/umi-kit-adapters as it only supports \@solana/kit v2
 */
function toKitInstruction(umiInstruction: UmiInstruction): Instruction {
  const accounts = umiInstruction.keys.map((key): AccountMeta => {
    let role: AccountRole;
    if (key.isSigner && key.isWritable) {
      role = AccountRole.WRITABLE_SIGNER;
    } else if (key.isSigner) {
      role = AccountRole.READONLY_SIGNER;
    } else if (key.isWritable) {
      role = AccountRole.WRITABLE;
    } else {
      role = AccountRole.READONLY;
    }
    return {
      address: address(key.pubkey),
      role,
    };
  });
  return {
    programAddress: address(umiInstruction.programId),
    accounts: accounts.length > 0 ? accounts : undefined,
    data: umiInstruction.data,
  };
}

async function getTokenProgramForMint(rpc: Rpc<SolanaRpcApi>, mint: Address): Promise<Address> {
  const mintInfo = await rpc.getAccountInfo(mint, { encoding: "base64" }).send();
  if (!mintInfo.value) {
    throw new Error(`Mint account ${mint} not found`);
  }
  const { owner } = mintInfo.value;
  if (owner === TOKEN_2022_PROGRAM_ADDRESS) return TOKEN_2022_PROGRAM_ADDRESS;
  if (owner === TOKEN_PROGRAM_ADDRESS) return TOKEN_PROGRAM_ADDRESS;
  throw new Error(`Account ${mint} is not a token mint (owned by ${owner})`);
}

export async function createSPLTransactionInstruction(
  rpc: Rpc<SolanaRpcApi>,
  receiver: string,
  sender: string,
  selectedToken: CustomTokenInfo | SolanaNftInfo,
  amount: bigint,
  umi: Umi
): Promise<Instruction[]> {
  const tokenMintAddress = isCustomTokenInfo(selectedToken) ? selectedToken.tokenAddress : selectedToken.contractAddress;
  const decimals = isCustomTokenInfo(selectedToken) ? Number(selectedToken.decimals) : 0;

  const isPNFT = !isCustomTokenInfo(selectedToken) && selectedToken.nftStandard?.toLowerCase() === "programmablenonfungible";
  // PNFT need to use metaplex instruction
  // https://developers.metaplex.com/token-metadata/token-standard#the-programmable-non-fungible-standard
  if (isPNFT) {
    const mintId = publicKey(tokenMintAddress);
    // Fetch the pNFT Asset with the Token Account
    const assetWithToken = await fetchDigitalAssetWithAssociatedToken(umi, mintId, publicKey(sender));
    const destinationAddress = publicKey(receiver);

    const associatedTokenProgramId = umi.programs.getPublicKey("splAssociatedToken");
    const tokenProgramIdResolved = umi.programs.getPublicKey("splToken");
    const destinationTokenAccount = umi.eddsa.findPda(associatedTokenProgramId, [
      publicKeyUmiSerializer().serialize(destinationAddress),
      publicKeyUmiSerializer().serialize(tokenProgramIdResolved),
      publicKeyUmiSerializer().serialize(mintId),
    ]);

    // Calculates the destinations wallet's Token Record Account.
    const destinationTokenRecord = findTokenRecordPda(umi, {
      mint: mintId,
      token: destinationTokenAccount[0],
    });

    const metaplexIns = transferV1(umi, {
      mint: mintId,
      destinationOwner: destinationAddress,
      destinationTokenRecord,
      tokenRecord: assetWithToken.tokenRecord?.publicKey,
      tokenStandard: TokenStandard.ProgrammableNonFungible,
      // Check to see if the pNFT asset as auth rules.
      authorizationRules: unwrapOptionRecursively(assetWithToken.metadata.programmableConfig)?.ruleSet || undefined,
      // Auth rules program ID
      authorizationRulesProgram: getMplTokenAuthRulesProgramId(umi),
      // Some pNFTs may require authorization data if set.
      authorizationData: undefined,
    }).getInstructions();

    // Convert Umi instructions to Kit Instruction format
    return metaplexIns.map((ix) => toKitInstruction(ix) as Instruction);
  }

  const mintAddr = address(tokenMintAddress);
  const senderAddr = address(sender);
  const receiverAddr = address(receiver);
  // Use a single signer instance for the sender so deduplication does not see "multiple distinct signers" for the same address.
  const senderSigner = createNoopSigner(senderAddr);

  // Detect the correct token program (Token or Token-2022)
  const tokenProgram = await getTokenProgramForMint(rpc, mintAddr);

  // Find associated token accounts using PDA derivation
  const [sourceTokenAccount] = await findAssociatedTokenPda({ mint: mintAddr, owner: senderAddr, tokenProgram });
  let associatedTokenAccount = receiverAddr;

  const instructions: Instruction[] = [];
  try {
    const [destAta] = await findAssociatedTokenPda({ mint: mintAddr, owner: receiverAddr, tokenProgram });
    associatedTokenAccount = destAta;
  } catch (e) {
    log.warn("error getting associatedTokenAccount, account passed is possibly a token account", e);
  }

  const receiverAccountInfo = await rpc.getAccountInfo(associatedTokenAccount, { encoding: "base64" }).send();

  const isValidTokenAccount =
    receiverAccountInfo.value &&
    (receiverAccountInfo.value.owner === TOKEN_PROGRAM_ADDRESS || receiverAccountInfo.value.owner === TOKEN_2022_PROGRAM_ADDRESS);

  if (!isValidTokenAccount) {
    const createAtaIx = getCreateAssociatedTokenIdempotentInstruction({
      payer: senderSigner,
      owner: receiverAddr,
      mint: mintAddr,
      ata: associatedTokenAccount,
      tokenProgram,
    });
    instructions.push(createAtaIx);
  }

  const transferIx = getTransferCheckedInstruction(
    {
      source: sourceTokenAccount,
      mint: mintAddr,
      destination: associatedTokenAccount,
      authority: senderSigner,
      amount,
      decimals,
    },
    { programAddress: tokenProgram }
  );
  instructions.push(transferIx);
  return instructions;
}

// ============================================================================
// Compute Units & Optimal Transaction
// ============================================================================

export const getSimulationComputeUnits = async (rpc: Rpc<SolanaRpcApi>, instructions: Array<Instruction>, payer: Address) => {
  const testInstructions = [getSetComputeUnitLimitInstruction({ units: 1400000 }), ...instructions];

  const { value: latestBlockhash } = await rpc.getLatestBlockhash().send();

  const testMsg = pipe(
    createTransactionMessage({ version: 0 }),
    (m) => setTransactionMessageFeePayer(payer, m),
    (m) => setTransactionMessageLifetimeUsingBlockhash(latestBlockhash, m),
    (m) => appendTransactionMessageInstructions(testInstructions, m)
  );

  const testTransaction = compileTransaction(testMsg);
  const base64Tx = getBase64EncodedWireTransaction(testTransaction);

  const rpcResponse = await rpc
    .simulateTransaction(base64Tx, {
      encoding: "base64",
      replaceRecentBlockhash: true,
      sigVerify: false,
    })
    .send();

  return rpcResponse.value.unitsConsumed ? Number(rpcResponse.value.unitsConsumed) : null;
};
/**
 * Builds an optimal transaction with priority fee for not getting transaction dropped
 * ref: https://solana.stackexchange.com/questions/9285/how-can-i-analyze-the-reason-for-frequent-blockheightexceeded-errors/9346#9346
 */
export async function buildOptimalTransaction(rpc: Rpc<SolanaRpcApi>, instructions: Array<Instruction>, sender: Address) {
  const priorityFees = await rpc.getRecentPrioritizationFees().send();
  // Use median of non-zero fees instead of Math.min — min almost always yields 0
  // which causes transactions to be dropped during network congestion.
  const allPriorityFee = priorityFees.map((x) => Number(x.prioritizationFee)).filter((fee) => fee > 0);
  const sorted = allPriorityFee.sort((a, b) => a - b);
  const microLamports = sorted.length > 0 ? sorted[Math.floor(sorted.length * 0.5)] : 0;

  const [units, recentBlockhash] = await Promise.all([
    getSimulationComputeUnits(rpc, instructions, sender),
    rpc.getLatestBlockhash({ commitment: "finalized" }).send(),
  ]);

  const finalInstructions: Instruction[] = [];
  finalInstructions.push(getSetComputeUnitPriceInstruction({ microLamports }));
  if (units) {
    finalInstructions.push(getSetComputeUnitLimitInstruction({ units: units + 1000 }));
  }
  finalInstructions.push(...instructions);

  const txMsg = pipe(
    createTransactionMessage({ version: 0 }),
    (m) => setTransactionMessageFeePayer(sender, m),
    (m) => setTransactionMessageLifetimeUsingBlockhash(recentBlockhash.value, m),
    (m) => appendTransactionMessageInstructions(finalInstructions, m)
  );

  return {
    transaction: compileTransaction(txMsg),
    recentBlockhash: recentBlockhash.value,
  };
}

export async function calculateTxFee(tx: Transaction, rpc: Rpc<SolanaRpcApi>): Promise<{ fee: number }> {
  const feeResult = await rpc.getFeeForMessage(getMessageBytesBase64(tx), { commitment: "confirmed" }).send();
  return { fee: Number(feeResult.value) || 0 };
}

// ============================================================================
// SPL Transaction Generator
// ============================================================================

export async function generateSPLTransaction(
  receiver: string,
  amount: bigint,
  selectedToken: CustomTokenInfo | SolanaNftInfo,
  sender: string,
  rpc: Rpc<SolanaRpcApi>,
  umi: Umi
): Promise<Transaction> {
  const instructions = await createSPLTransactionInstruction(rpc, receiver, sender, selectedToken, amount, umi);
  const result = await buildOptimalTransaction(rpc, instructions, address(sender));
  return result.transaction;
}
